import axios from 'axios';
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  removeAllDriveDocumentLabels,
  removeDriveDocumentLabels,
  UserKeys,
} from '../../../../api/users-api';
import FilesPreviewSection from '../../../../shared/files-preview-section';
import ChangeLogReason from '../../../../shared/change-log-reason';
import GeneralDialog from '../../../../shared/general-dialog';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import { useStore } from '../../../state/zustand-store';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { ApiException, Doc } from '../../../../../types';
import { handleErrors } from '../../../../utils/errors';
import { Messages } from '../../../../utils/message-constants';

import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { WarningAlert } from '../../../shared/warning-alert';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SvgIcon from '@mui/material/SvgIcon';
import { useTheme } from '@mui/material/styles';
import ChevronRight from '@mui/icons-material/ChevronRight';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';

export interface LabelActionConfig {
  displayName: string;
  description: string;
  actionType: 'removeSpecific' | 'removeAll' | 'addLabels';
  icon?: React.ReactNode;
  role?: string;
  disabled?: boolean;
  disabledReason?: string;
}

interface DriveLabelsActionsDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedDocs: Doc[];
  changeLogRoles: string[];
  driveRoles: string[];
  actions?: LabelActionConfig[];
  contentDescription?: string;
  onActionComplete?: (actionType: string, affectedCount: number) => void;
}

interface LabelOption {
  id: string;
  title: string;
}

type DialogStep = 'menu' | 'removeSpecific' | 'removeAll' | 'addLabels';

function parseDriveLabelBatchCounts(errorResponse?: ApiException | null) {
  if (!errorResponse) {
    return null;
  }

  if (
    errorResponse.successCount !== undefined &&
    errorResponse.failureCount !== undefined
  ) {
    return {
      successCount: errorResponse.successCount,
      failureCount: errorResponse.failureCount,
    };
  }

  return null;
}

const DEFAULT_ACTIONS: LabelActionConfig[] = [
  {
    displayName: 'Remove Drive Label',
    description:
      '<span style="font-weight:bold;color:inherit;">Remove</span> a specific label from all selected files. Files without the label will be skipped.',
    actionType: 'removeSpecific',
    icon: <DeleteOutline sx={{ color: 'text.secondary', fontSize: 22 }} />,
    role: DriveRoles.CanRemoveDriveLabels.value,
  },
  {
    displayName: 'Remove ALL Drive Labels',
    description:
      '<span style="font-weight:bold;color:inherit;">Remove all</span> labels from all selected files. Files without any labels will be skipped.',
    actionType: 'removeAll',
    icon: (
      <RemoveCircleOutline sx={{ color: 'text.secondary', fontSize: 22 }} />
    ),
    role: DriveRoles.CanRemoveDriveLabels.value,
  },
];

export default function DriveLabelsActionsDialog({
  open,
  handleClose,
  userEmail,
  selectedDocs,
  changeLogRoles,
  driveRoles,
  actions = DEFAULT_ACTIONS,
  contentDescription = 'Select the action you want to perform on the selected files',
  onActionComplete,
}: DriveLabelsActionsDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const { changeLogLength } = useUserSettings();

  const [currentStep, setCurrentStep] = useState<DialogStep>('menu');
  const [selectedLabels, setSelectedLabels] = useState<Set<string>>(new Set());
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const availableLabels = useMemo(() => {
    const labelMap = new Map<string, LabelOption>();

    selectedDocs.forEach((doc) => {
      const resolvedIds = doc.labelIds ?? [];
      if (resolvedIds.length > 0) {
        resolvedIds.forEach((labelId, index) => {
          const title = doc.labelTitles?.[index] ?? labelId;
          if (!labelMap.has(labelId)) {
            labelMap.set(labelId, { id: labelId, title });
          }
        });
      }
    });

    return Array.from(labelMap.values());
  }, [selectedDocs]);

  const labelsToRemove = useMemo(() => {
    if (currentStep === 'removeAll') {
      return availableLabels;
    }
    return availableLabels.filter((label) => selectedLabels.has(label.id));
  }, [availableLabels, currentStep, selectedLabels]);

  const affectedDocs = useMemo(() => {
    if (currentStep === 'removeAll') {
      return selectedDocs.filter((doc) => {
        const resolvedIds = doc.labelIds ?? [];
        return resolvedIds.length > 0;
      });
    }
    return selectedDocs.filter((doc) => {
      const resolvedIds = doc.labelIds ?? [];
      return resolvedIds.some((id) => selectedLabels.has(id));
    });
  }, [currentStep, selectedDocs, selectedLabels]);

  const hasPublishedLabels = selectedDocs.some((doc) => {
    const resolvedIds = doc.labelIds ?? [];
    return resolvedIds.length > 0;
  });

  const labelIdsByDocIdForRemoveAll = useMemo(() => {
    if (currentStep !== 'removeAll') {
      return {} as Record<string, string[]>;
    }

    return affectedDocs.reduce<Record<string, string[]>>((acc, doc) => {
      const resolvedIds = doc.labelIds ?? [];
      if (resolvedIds.length > 0) {
        acc[doc.id] = resolvedIds;
      }
      return acc;
    }, {});
  }, [affectedDocs, currentStep]);

  const labelIdsByDocIdForRemoveSpecific = useMemo(() => {
    if (currentStep !== 'removeSpecific') {
      return {} as Record<string, string[]>;
    }

    return affectedDocs.reduce<Record<string, string[]>>((acc, doc) => {
      const docLabels = doc.labelIds ?? [];
      const labelsToRemove = docLabels.filter((labelId) =>
        selectedLabels.has(labelId)
      );
      if (labelsToRemove.length > 0) {
        acc[doc.id] = labelsToRemove;
      }
      return acc;
    }, {});
  }, [affectedDocs, currentStep, selectedLabels]);

  const { mutate, isPending: isProcessing } = useMutation({
    mutationFn: async () => {
      if (currentStep === 'removeAll') {
        return removeAllDriveDocumentLabels(
          userEmail,
          affectedDocs.map((doc) => doc.id),
          labelIdsByDocIdForRemoveAll,
          changeLogMessage
        );
      }

      if (currentStep === 'removeSpecific') {
        return removeDriveDocumentLabels(
          userEmail,
          affectedDocs.map((doc) => doc.id),
          labelIdsByDocIdForRemoveSpecific,
          changeLogMessage
        );
      }

      return undefined;
    },
    onSuccess: (response) => {
      const executedDirectly = response?.executedDirectly === true;

      const successMessage = Messages.DriveLabels.RemoveSuccess(
        currentStep === 'removeAll',
        labelsToRemove.length
      );
      const taskMessage = Messages.DriveLabels.RemoveTask(
        currentStep === 'removeAll',
        labelsToRemove.length
      );

      setSnackbar({
        open: true,
        severity: executedDirectly ? 'success' : 'info',
        message: executedDirectly ? successMessage : taskMessage,
      });
      onActionComplete?.(currentStep, affectedDocs.length);
      handleCloseAndReset();
      if (executedDirectly) {
        queryClient.invalidateQueries({ queryKey: [UserKeys.DriveDocuments] });
        queryClient.invalidateQueries({ queryKey: [UserKeys.DriveFolders] });
      }
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException | undefined;
        const isPartialFailure =
          errorResponse?.exceptionType === 'DriveLabelRemovalPartialFailure';
        const counts = parseDriveLabelBatchCounts(errorResponse);

        if (isPartialFailure && counts) {
          if (counts.successCount > 0) {
            setSnackbar({
              open: true,
              severity: 'warning',
              message:
                'Drive label removal partially completed. Success: ' +
                counts.successCount +
                ', Failed: ' +
                counts.failureCount +
                '.',
            });
            onActionComplete?.(currentStep, counts.successCount);
            handleCloseAndReset();
            queryClient.invalidateQueries({
              queryKey: [UserKeys.DriveDocuments],
            });
            queryClient.invalidateQueries({
              queryKey: [UserKeys.DriveFolders],
            });
            return;
          } else {
            // All failed, show generic error
            handleErrors({
              error,
              snackbarMessage: Messages.Errors.Network.Processing('labels'),
            });
            return;
          }
        }
      }
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Processing('labels'),
      });
    },
    onSettled: () => {
      createEvent(
        currentStep === 'removeAll'
          ? analytics.drive.drive_explorer.remove_all_drive_labels
          : analytics.drive.drive_explorer.remove_drive_labels,
        {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.driveExplorer,
        }
      );
    },
  });

  const hasRemoveDriveLabelsPermission = isAbleToSeeUIElement(
    driveRoles,
    DriveRoles.CanRemoveDriveLabels.value
  );

  const isChangeLogRequired = isAbleToSeeUIElement(
    changeLogRoles,
    DriveRoles.CanRemoveDriveLabels.value
  );

  const actionsWithPermission = useMemo(() => {
    return actions.map((action) => {
      if (
        action.role === DriveRoles.CanRemoveDriveLabels.value &&
        !hasRemoveDriveLabelsPermission
      ) {
        return {
          ...action,
          disabled: true,
          disabledReason: Messages.Errors.Permission.Acting(
            DriveRoles.CanRemoveDriveLabels.name
          ),
        };
      }
      return action;
    });
  }, [actions, hasRemoveDriveLabelsPermission]);

  const isInvalidSelection =
    currentStep === 'removeSpecific'
      ? selectedLabels.size === 0 || affectedDocs.length === 0
      : currentStep === 'removeAll'
      ? affectedDocs.length === 0
      : true;

  function handleCloseAndReset() {
    setCurrentStep('menu');
    setSelectedLabels(new Set());
    setChangeLogMessage('');
    handleClose();
  }

  function handleBack() {
    setCurrentStep('menu');
    setSelectedLabels(new Set());
  }

  function handleSelectAction(actionType: DialogStep) {
    if (actionType !== 'removeSpecific' && actionType !== 'removeAll') {
      return;
    }
    setCurrentStep(actionType);
    setSelectedLabels(new Set());
  }

  function handleSubmit() {
    if (currentStep !== 'removeSpecific' && currentStep !== 'removeAll') {
      return;
    }

    if (currentStep === 'removeSpecific' && selectedLabels.size === 0) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'Please select at least one label to remove.',
      });
      return;
    }

    if (affectedDocs.length === 0) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message:
          currentStep === 'removeAll'
            ? 'No selected files currently have labels to remove.'
            : 'None of the selected files contain the selected label(s).',
      });
      return;
    }

    if (isChangeLogRequired && changeLogMessage.length < changeLogLength) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: `Please provide a change log reason (minimum ${changeLogLength} characters).`,
      });
      return;
    }

    mutate();
  }

  const isMenuStep = currentStep === 'menu';

  const dialogContent = isMenuStep ? (
    <MenuContent
      actions={actionsWithPermission}
      hasPublishedLabels={hasPublishedLabels}
      selectedDocsCount={selectedDocs.length}
      contentDescription={contentDescription}
      onSelectAction={handleSelectAction}
    />
  ) : currentStep === 'removeSpecific' ? (
    <RemoveSpecificContent
      selectedDocsCount={selectedDocs.length}
      availableLabels={availableLabels}
      selectedLabels={selectedLabels}
      setSelectedLabels={setSelectedLabels}
      labelsToRemove={labelsToRemove}
      affectedDocs={affectedDocs}
      isChangeLogRequired={isChangeLogRequired}
      changeLogMessage={changeLogMessage}
      setChangeLogMessage={setChangeLogMessage}
      changeLogLength={changeLogLength}
    />
  ) : (
    <RemoveAllContent
      selectedDocsCount={selectedDocs.length}
      labelsToRemove={labelsToRemove}
      affectedDocs={affectedDocs}
      isChangeLogRequired={isChangeLogRequired}
      changeLogMessage={changeLogMessage}
      setChangeLogMessage={setChangeLogMessage}
      changeLogLength={changeLogLength}
    />
  );

  return (
    <>
      <GeneralDialog
        open={open}
        handleClose={handleCloseAndReset}
        maxWidth="sm"
        title=""
        dialogContent={dialogContent}
        primaryButton={
          !isMenuStep
            ? {
                title: 'REMOVE LABELS',
                action: handleSubmit,
                loading: isProcessing,
                disabled:
                  isInvalidSelection ||
                  isProcessing ||
                  (isChangeLogRequired &&
                    changeLogMessage.length < changeLogLength),
                color: 'warning',
              }
            : undefined
        }
        secondaryButton={{
          title: 'CANCEL',
          action: handleCloseAndReset,
          loading: false,
          disabled: isProcessing,
          shown: true,
          alignLeft: true,
          sx: {
            color: 'text.secondary',
            borderColor: 'text.secondary',
            background: 'transparent',
          },
        }}
        tertiaryButton={{
          title: 'BACK',
          action: handleBack,
          loading: false,
          disabled: isProcessing,
          shown: !isMenuStep,
          variant: 'text',
          color: 'primary',
          sx: {
            color: 'primary.main',
            background: 'transparent',
            border: 'none',
            minWidth: 'auto',
          },
        }}
      />
    </>
  );
}

interface HeaderProps {
  selectedDocsCount: number;
}

function DialogHeader({ selectedDocsCount }: HeaderProps) {
  const theme = useTheme();
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
        <SvgIcon
          sx={{
            mr: 1.5,
            mt: 1.7,
            fontSize: 28,
            color: 'text.primary',
            width: 22,
            height: 22,
          }}
        >
          {theme.palette.mode === 'light' ? (
            <path
              d="M24 0.000206642V24.0002L0 0.000206642H24Z"
              fill="#233166"
            />
          ) : (
            <path
              d="M24 0.000206642V24.0002L0 0.000206642H24Z"
              fill="#5D75D1"
            />
          )}
        </SvgIcon>
        <Box>
          <Typography
            variant="h6"
            sx={{ color: 'text.primary', fontWeight: 600 }}
          >
            Manage Drive Labels
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
          >
            {selectedDocsCount} File{selectedDocsCount === 1 ? '' : 's'}{' '}
            Selected
          </Typography>
        </Box>
      </Box>

      <Divider
        sx={{
          width: '600px',
          borderTop: '1px solid',
          borderColor: 'divider',
          mx: -3,
        }}
      />
    </>
  );
}

interface MenuContentProps {
  actions: LabelActionConfig[];
  hasPublishedLabels: boolean;
  selectedDocsCount: number;
  contentDescription: string;
  onSelectAction: (actionType: DialogStep) => void;
}

function MenuContent({
  actions,
  hasPublishedLabels,
  selectedDocsCount,
  contentDescription,
  onSelectAction,
}: MenuContentProps) {
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        borderRadius: '4px',
      }}
    >
      <DialogHeader selectedDocsCount={selectedDocsCount} />

      <Box
        sx={{
          width: '100%',
          maxHeight: '450px', // Fixed height for scrollable content
          minHeight: '450px',
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            pt: 2,
            pb: 1,
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '16px',
            alignSelf: 'stretch',
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {contentDescription}
          </Typography>

          <Stack spacing={1} sx={{ width: '100%' }}>
            {actions.map((action) => {
              const isDisabled =
                action.disabled || (!hasPublishedLabels && !action.disabled);

              const cardContent = (
                <Card
                  variant="outlined"
                  sx={{
                    display: 'flex',
                    padding: '8px 8px 8px 16px',
                    alignItems: 'flex-start',
                    gap: '16px',
                    alignSelf: 'stretch',
                    borderColor: 'divider',
                    bgcolor: !isDisabled
                      ? 'transparent'
                      : 'action.disabledBackground',
                    cursor: !isDisabled ? 'pointer' : 'not-allowed',
                    opacity: !isDisabled ? 1 : 0.6,
                    borderRadius: 1,
                    transition: 'all 0.2s',
                    '&:hover': !isDisabled
                      ? {
                          borderColor: 'primary.main',
                        }
                      : {},
                  }}
                  onClick={() => {
                    if (!isDisabled) {
                      onSelectAction(action.actionType as DialogStep);
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      bgcolor: 'action.hover',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mt: 0.25,
                      flexShrink: 0,
                    }}
                  >
                    {action.icon}
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 400,
                        color: 'text.primary',
                        mb: 0.5,
                      }}
                    >
                      {action.displayName}
                    </Typography>
                    {/*
                      Using dangerouslySetInnerHTML is safe here because the description content is static,
                      fully controlled, and does not contain any user-generated or external input.
                      Only simple formatting tags (e.g., <strong>) are used for emphasis.
                    */}
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                      component="span"
                      dangerouslySetInnerHTML={{ __html: action.description }}
                    />
                  </Box>

                  <ChevronRight
                    sx={{ mr: 1.5, mt: 1, color: 'text.secondary' }}
                  />
                </Card>
              );

              if (isDisabled) {
                return (
                  <Tooltip
                    key={action.actionType}
                    title={
                      action.disabledReason ||
                      'This action is not available for your current selection.'
                    }
                    arrow
                    placement="top"
                  >
                    <span>{cardContent}</span>
                  </Tooltip>
                );
              }

              return <Box key={action.actionType}>{cardContent}</Box>;
            })}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

interface RemoveSpecificContentProps {
  selectedDocsCount: number;
  availableLabels: LabelOption[];
  selectedLabels: Set<string>;
  setSelectedLabels: (labels: Set<string>) => void;
  labelsToRemove: LabelOption[];
  affectedDocs: Doc[];
  isChangeLogRequired: boolean;
  changeLogMessage: string;
  setChangeLogMessage: (message: string) => void;
  changeLogLength: number;
}

function RemoveSpecificContent({
  selectedDocsCount,
  availableLabels,
  selectedLabels,
  setSelectedLabels,
  labelsToRemove,
  affectedDocs,
  isChangeLogRequired,
  changeLogMessage,
  setChangeLogMessage,
  changeLogLength,
}: RemoveSpecificContentProps) {
  const selectedLabelOptions = availableLabels
    .filter((label) => selectedLabels.has(label.id))
    .sort((a, b) => a.title.localeCompare(b.title));

  const selectableLabelOptions = availableLabels
    .filter((label) => !selectedLabels.has(label.id))
    .sort((a, b) => a.title.localeCompare(b.title));

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        borderRadius: '4px',
      }}
    >
      <DialogHeader selectedDocsCount={selectedDocsCount} />

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          pt: 2,
          pb: 1,
          minHeight: '450px',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: '16px',
          alignSelf: 'stretch',
          boxSizing: 'border-box',
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          Search and select the label(s) you want to remove.
        </Typography>

        {selectedLabelOptions.length > 0 ? (
          <WarningAlert
            title="This action cannot be undone"
            subtitle={`Once confirmed, label changes will be applied immediately and cannot be reversed.\nSome labels here are managed by your organization's policies and can't be added or removed. Those changes will be skipped.`}
            severity="warning"
            icon={<WarningAmberOutlined fontSize="small" />}
          />
        ) : null}

        <Box sx={{ width: '100%' }}>
          <Autocomplete
            multiple
            options={selectableLabelOptions}
            getOptionLabel={(option) => option.title}
            value={selectedLabelOptions}
            disableClearable
            onChange={(_, newValue) => {
              const newSelected = new Set(newValue.map((value) => value.id));
              setSelectedLabels(newSelected);
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Search and select a label" />
            )}
            renderTags={() => null}
          />
        </Box>

        {selectedLabelOptions.length > 0 ? (
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ flexWrap: 'wrap', rowGap: 0.75 }}
          >
            {selectedLabelOptions.map((option) => (
              <Chip
                key={option.id}
                label={option.title}
                size="small"
                onDelete={() => {
                  const updated = new Set(selectedLabels);
                  updated.delete(option.id);
                  setSelectedLabels(updated);
                }}
              />
            ))}
          </Stack>
        ) : null}

        {labelsToRemove.length > 0 && (
          <FilesPreviewSection
            affectedDocs={affectedDocs}
            highlightLabels={labelsToRemove.map((label) => label.title)}
          />
        )}

        {isChangeLogRequired && labelsToRemove.length > 0 && (
          <ChangeLogReason
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            changeLogLength={changeLogLength}
          />
        )}
      </Box>
    </Box>
  );
}

interface RemoveAllContentProps {
  selectedDocsCount: number;
  labelsToRemove: LabelOption[];
  affectedDocs: Doc[];
  isChangeLogRequired: boolean;
  changeLogMessage: string;
  setChangeLogMessage: (message: string) => void;
  changeLogLength: number;
}

function RemoveAllContent({
  selectedDocsCount,
  labelsToRemove,
  affectedDocs,
  isChangeLogRequired,
  changeLogMessage,
  setChangeLogMessage,
  changeLogLength,
}: RemoveAllContentProps) {
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        borderRadius: '4px',
      }}
    >
      <DialogHeader selectedDocsCount={selectedDocsCount} />

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          pt: 2,
          pb: 1,
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: '16px',
          alignSelf: 'stretch',
          boxSizing: 'border-box',
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          Preview and Review labels to be removed
        </Typography>

        <WarningAlert
          title="This action cannot be undone"
          subtitle={`Once confirmed, label changes will be applied immediately and cannot be reversed.\nSome labels here are managed by your organization's policies and can't be added or removed. Those changes will be skipped.`}
          severity="warning"
          icon={<WarningAmberOutlined fontSize="small" />}
        />

        <FilesPreviewSection
          affectedDocs={affectedDocs}
          highlightLabels={labelsToRemove.map((label) => label.title)}
        />

        {isChangeLogRequired && (
          <ChangeLogReason
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            changeLogLength={changeLogLength}
          />
        )}
      </Box>
    </Box>
  );
}
